import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Card, Tooltip, Spin } from 'antd';
import { WiDaySunny, WiCloud, WiRain, WiRaindrop } from 'weather-icons-react';
import { useTheme } from '../../contexts/useTheme';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useSelectedTimestamp } from '../../contexts/useSelectedTimestamp';
import { useMinuteClock } from '../../hooks/useMinuteClock';
import { usePermissions } from '../../hooks/usePermissions';
import { useRainForecastData } from '../../hooks/useRainForecastData';
import { useCollectionFreshness } from '../../hooks/useCollectionFreshness';
import { isEventActive as checkEventActive } from '../../utils/eventSource';
import { resolveActiveRainForecast } from '../../utils/rainForecastSelection';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Rain status colors from legacy portal
const RAIN_COLORS = {
  green: '#2dd598',   // No rain expected
  orange: '#ff8e01',  // Rain expected
  red: '#ff3b3c',     // Currently raining
  yellow: '#e4e14d',  // Drops at times
};

// Get icon component based on rain status
const getRainStatusIcon = (status: 0 | 1 | 2 | 3, color: string, size: number = 24) => {
  switch (status) {
    case 0: // No rain - sunny/clear
      return <WiDaySunny size={size} color={color} />;
    case 1: // Rain expected - cloud
      return <WiCloud size={size} color={color} />;
    case 2: // Currently raining - rain
      return <WiRain size={size} color={color} />;
    case 3: // Drops at times - raindrop
      return <WiRaindrop size={size} color={color} />;
    default:
      return <WiDaySunny size={size} color={color} />;
  }
};

// Calculate rain status based on rain_type and few_drops (legacy logic)
const getRainStatus = (rainType: number | null, fewDrops: boolean | null): 0 | 1 | 2 | 3 => {
  // Status 3: Drops at times (rain_type = 0 but few_drops = true)
  if (rainType === 0 && fewDrops) {
    return 3;
  }
  // Status 2: Currently raining
  if (rainType === 2) {
    return 2;
  }
  // Status 1: Rain expected
  if (rainType === 1) {
    return 1;
  }
  // Status 0: No rain
  return 0;
};

// Get the color based on rain status
const getStatusColor = (status: 0 | 1 | 2 | 3): string => {
  switch (status) {
    case 0: return RAIN_COLORS.green;
    case 1: return RAIN_COLORS.orange;
    case 2: return RAIN_COLORS.red;
    case 3: return RAIN_COLORS.yellow;
    default: return RAIN_COLORS.green;
  }
};

// Get background color with opacity for the card
const getCardBackground = (status: 0 | 1 | 2 | 3, isDark: boolean): string => {
  const color = getStatusColor(status);
  // Use slightly different opacity for dark/light themes
  const opacity = isDark ? '0.25' : '0.2';
  
  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

interface RainForecastHeaderProps {
  compact?: boolean;
}

export const RainForecastHeader: React.FC<RainForecastHeaderProps> = ({ compact = false }) => {
  const { theme } = useTheme();
  const { selectedEvent } = useEventSelection();
  const { selectedTimestamp } = useSelectedTimestamp();
  const isDark = theme === 'dark';
  const now = useMinuteClock();
  const { isRoot } = usePermissions();

  const eventActive = checkEventActive(selectedEvent);
  const metadataTimestamps = useCollectionFreshness(['forecast_rain'], {
    interval: 30_000,
    enabled: eventActive,
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const lastFetchTimeRef = useRef<string>('');

  useEffect(() => {
    const latest = metadataTimestamps.forecast_rain;
    if (latest && latest > lastFetchTimeRef.current) {
      lastFetchTimeRef.current = latest;
      setRefreshCounter(c => c + 1);
    }
  }, [metadataTimestamps]);

  const { forecasts, loading } = useRainForecastData(selectedEvent, refreshCounter);

  const tz = selectedEvent?.circuit?.time_zone || 'UTC';

  const activeForecast = useMemo(
    () => {
      if (!selectedEvent) return null;
      return resolveActiveRainForecast({
        forecasts,
        anchorTime: selectedTimestamp,
        sessions: selectedEvent.sessions ?? [],
        circuitTimezone: tz,
        isRoot,
        currentTime: now,
      });
    },
    [forecasts, selectedTimestamp, selectedEvent, tz, isRoot, now],
  );

  // Don't render if no event is selected
  if (!selectedEvent) {
    return null;
  }

  // Format the update time
  const getUpdateTimeDisplay = () => {
    if (!activeForecast) return null;
    const dt = dayjs.utc(activeForecast.value_date).tz(tz);
    return `Updated: ${dt.format('HH:mm')} on ${dt.format('ddd DD MMM')}`;
  };

  // Calculate rain status for coloring
  const rainStatus = activeForecast 
    ? getRainStatus(activeForecast.rain_type, activeForecast.few_drops)
    : 0;
  const statusColor = getStatusColor(rainStatus);
  const cardBackground = getCardBackground(rainStatus, isDark);

  const cardStyle: React.CSSProperties = {
    background: cardBackground,
    border: `1px solid ${statusColor}40`,
    height: compact ? '32px' : '48px',
    minWidth: compact ? undefined : '150px',
    maxWidth: compact ? undefined : '220px',
    flexShrink: 0,
  };

  // Loading state
  if (loading) {
    return (
      <Card size="small" style={cardStyle} styles={{ body: { padding: compact ? '2px 6px' : '6px 12px', height: '100%' } }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8 }}>
          <Spin size="small" />
          {!compact && (
            <span style={{ fontSize: 11, color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>
              Loading rain forecast...
            </span>
          )}
        </div>
      </Card>
    );
  }

  // No data state - don't show anything
  if (!activeForecast) {
    return null;
  }

  const updateTimeDisplay = getUpdateTimeDisplay();

  // Create tooltip content with full details
  const tooltipContent = (
    <div style={{ maxWidth: 300 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Rain Forecast</div>
      {updateTimeDisplay && (
        <div style={{ fontSize: 11, marginBottom: 8, opacity: 0.8 }}>{updateTimeDisplay}</div>
      )}
      <div style={{ marginBottom: 8 }}>{activeForecast.message}</div>
      {activeForecast.rain_start_time && (
        <div style={{ fontSize: 11 }}>Start: {activeForecast.rain_start_time}</div>
      )}
      {activeForecast.rain_end_time && (
        <div style={{ fontSize: 11 }}>End: {activeForecast.rain_end_time}</div>
      )}
      {activeForecast.rain_intensity !== null && (
        <div style={{ fontSize: 11 }}>Intensity: Level {activeForecast.rain_intensity}</div>
      )}
      {activeForecast.rain_speed_kph !== null && (
        <div style={{ fontSize: 11 }}>Speed: {activeForecast.rain_speed_kph} kph</div>
      )}
    </div>
  );

  return (
    <Tooltip title={tooltipContent} placement="bottom">
      <Card size="small" style={cardStyle} styles={{ body: { padding: compact ? '2px 6px' : '4px 12px', height: '100%' } }}>
        {compact ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            {getRainStatusIcon(rainStatus, statusColor, 20)}
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            height: '100%', 
            gap: 10,
          }}>
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {getRainStatusIcon(rainStatus, statusColor, 28)}
            </div>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center',
              minWidth: 0,
              flex: 1,
            }}>
              {updateTimeDisplay && (
                <div
                  style={{
                    fontSize: '9px',
                    fontWeight: 500,
                    color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                    lineHeight: '1.2',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {updateTimeDisplay}
                </div>
              )}
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: isDark ? 'white' : 'black',
                  lineHeight: '1.3',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {activeForecast.message}
              </div>
            </div>
          </div>
        )}
      </Card>
    </Tooltip>
  );
};

